import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { Currency } from 'react-intl-number-format'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'

import { useOrders } from '../../hooks/useOrders'
import { theme } from '../../theme'
import { getBankTransferDetailsFromOrder } from '../../utils/bankTransfer'
import { hasShortDisplayVariant, shortenOrderNumber } from '../../utils/orderNumber'
import { copyTextToClipboard } from '../../utils/clipboard'
import { downloadOrderProforma } from '../../utils/orderDocument'
import { getAvailabilityBreakdown, hasOnOrderQuantity } from '../../utils/itemPresentation'
import BankTransferDetailsCard from '../Orders/BankTransferDetailsCard'
import BankTransferProofUploader from '../Orders/BankTransferProofUploader'

const paymentLabels = {
  awaiting_payment: 'În așteptarea plății',
  paid: 'Plătită',
  payment_failed: 'Plata a eșuat',
  refunded: 'Rambursată'
}

const fulfillmentLabels = {
  new: 'Nouă',
  supplier_waiting: 'În așteptare furnizor',
  processing: 'În procesare',
  out_for_delivery: 'În livrare',
  completed: 'Finalizată',
  cancelled: 'Anulată'
}

const paymentChipColor = {
  awaiting_payment: 'warning',
  paid: 'success',
  payment_failed: 'error',
  refunded: 'default'
}

const fulfillmentChipColor = {
  new: 'default',
  supplier_waiting: 'warning',
  processing: 'info',
  out_for_delivery: 'info',
  completed: 'success',
  cancelled: 'error'
}

const bankTransferLabels = {
  awaiting_proof: 'Asteapta dovada',
  proof_uploaded: 'Dovada trimisa',
  confirmed: 'Plata confirmata',
  rejected: 'Dovada respinsa',
  not_applicable: 'N/A'
}

const bankTransferChipColor = {
  awaiting_proof: 'warning',
  proof_uploaded: 'info',
  confirmed: 'success',
  rejected: 'error',
  not_applicable: 'default'
}

const PAGE_SIZE = 5

const getPrimaryActionSx = () => ({
  backgroundColor: `${theme.palette.accent.main} !important`,
  color: '#FFFFFF !important',
  border: `1px solid ${theme.palette.accent.main} !important`,
  borderRadius: '9px',
  whiteSpace: 'nowrap',
  minHeight: '30px',
  px: 1.05,
  py: 0.35,
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1.7,
  '&:hover': {
    backgroundColor: `${theme.palette.accent.light} !important`,
    borderColor: `${theme.palette.accent.light} !important`
  }
})

const getSecondaryActionSx = () => ({
  backgroundColor: '#F5F5F5 !important',
  color: `${theme.palette.fontColor.dark} !important`,
  border: `1px solid ${theme.palette.borderColor.main} !important`,
  borderRadius: '9px',
  whiteSpace: 'nowrap',
  boxShadow: 'none !important',
  minHeight: '30px',
  px: 1.05,
  py: 0.35,
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1.7,
  '&:hover': {
    backgroundColor: '#EBEBEB !important',
    borderColor: `${theme.palette.fontColor.main} !important`
  }
})

const getFinalInvoiceUrl = (order) => (
  order?.final_invoice?.download_url || order?.final_invoice_download_url || ''
)

const getStripePaymentDocumentUrl = (order) => (
  order?.stripe_receipt_url || order?.stripe_invoice_pdf || order?.stripe_invoice_url || ''
)

const canShowFinalInvoice = (order) => Boolean(order?.customer_invoice_available && getFinalInvoiceUrl(order))

const canShowStripePaymentDocument = (order) => Boolean(
  order?.customer_invoice_available &&
  getStripePaymentDocumentUrl(order)
)

const isPaidBankTransferWithoutInvoice = (order) => (
  order?.payment_method === 'bank_transfer' &&
  order?.payment_status === 'paid' &&
  !canShowFinalInvoice(order)
)

const isPaidCardWithoutFinalInvoice = (order) => (
  order?.payment_method !== 'bank_transfer' &&
  order?.payment_status === 'paid' &&
  !canShowFinalInvoice(order)
)

const AvailabilitySplit = ({ item }) => {
  const availability = getAvailabilityBreakdown(item, item?.quantity)
  const chipSx = {
    height: '19px',
    '& .MuiChip-label': {
      px: 0.8,
      fontSize: '9px',
      fontWeight: 800,
      lineHeight: 1.5
    }
  }

  return (
    <Stack direction='row' spacing={0.6} flexWrap='wrap' useFlexGap>
      {availability.inStockQuantity > 0 && (
        <Chip
          size='small'
          label={`In stoc: ${availability.inStockQuantity}`}
          sx={{
            ...chipSx,
            color: '#008000',
            backgroundColor: 'rgba(0, 128, 0, 0.11)'
          }}
        />
      )}
      {availability.onOrderQuantity > 0 && (
        <Chip
          size='small'
          label={`La comanda: ${availability.onOrderQuantity}`}
          sx={{
            ...chipSx,
            color: theme.palette.accent.main,
            backgroundColor: 'rgba(226, 6, 19, 0.08)',
            border: `1px solid ${theme.palette.accent.main}`
          }}
        />
      )}
      {availability.quantity === 0 && item?.stock_label && (
        <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '10px', lineHeight: 1.9 }}>
          {item.stock_label}
        </Typography>
      )}
    </Stack>
  )
}

AvailabilitySplit.propTypes = {
  item: PropTypes.object
}

const getDocumentActions = (order) => {
  const actions = []

  if ((order?.payment_status === 'awaiting_payment' || order?.payment_status === 'payment_failed') && order?.stripe_checkout_url) {
    actions.push({
      key: 'resume-payment',
      label: order.payment_status === 'payment_failed' ? 'Reîncearcă plata' : 'Continuă plata',
      href: order.stripe_checkout_url,
      icon: <ReceiptLongOutlinedIcon />,
      variant: 'contained',
      sx: getPrimaryActionSx()
    })
  }

  if (canShowFinalInvoice(order)) {
    actions.push({
      key: 'final-invoice',
      label: 'Descarcă factura',
      href: getFinalInvoiceUrl(order),
      icon: <ReceiptLongOutlinedIcon />,
      variant: 'contained',
      sx: getPrimaryActionSx()
    })
    actions.push({
      key: 'proforma',
      label: 'Descarcă proforma',
      onClick: () => downloadOrderProforma(order),
      icon: <DownloadOutlinedIcon />,
      variant: 'contained',
      sx: getSecondaryActionSx()
    })
  } else {
    actions.push({
      key: 'proforma',
      label: 'Descarcă proforma',
      onClick: () => downloadOrderProforma(order),
      icon: <DownloadOutlinedIcon />,
      variant: 'contained',
      sx: getPrimaryActionSx()
    })
  }

  if (canShowStripePaymentDocument(order)) {
    actions.push({
      key: 'stripe-payment-proof',
      label: 'Dovada plata Stripe',
      href: getStripePaymentDocumentUrl(order),
      icon: <ReceiptLongOutlinedIcon />,
      variant: 'contained',
      sx: getSecondaryActionSx()
    })
  }

  return actions
}

const OrdersList = () => {
  const { fetchOrders } = useOrders()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const fetchOrdersRef = useRef(fetchOrders)
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [total, setTotal] = useState(0)
  const pageParam = Number.parseInt(searchParams.get('orders_page') || '1', 10)
  const page = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam
  const expandedOrderId = searchParams.get('expanded_order') || null

  const updateOrdersSearchParams = useMemo(() => {
    return (updates) => {
      const nextParams = new URLSearchParams(searchParams)
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '' || value === false || (key === 'orders_page' && Number(value) <= 1)) {
          nextParams.delete(key)
        } else {
          nextParams.set(key, String(value))
        }
      })

      const nextSearch = nextParams.toString()
      navigate({
        pathname: location.pathname || '/profilulmeu',
        search: nextSearch ? `?${nextSearch}` : '',
        hash: '#Orders'
      }, { replace: true })
    }
  }, [location.pathname, navigate, searchParams])

  useEffect(() => {
    fetchOrdersRef.current = fetchOrders
  }, [fetchOrders])

  useEffect(() => {
    let mounted = true

    const loadOrders = async () => {
      setLoading(true)
      setError('')
      try {
        const payload = await fetchOrdersRef.current({ page, limit: PAGE_SIZE })
        if (mounted) {
          const nextOrders = payload.data || []
          setOrders(nextOrders)
          setTotal(Number(payload.total || 0))
          if (expandedOrderId && !nextOrders.some(order => order._id === expandedOrderId)) {
            updateOrdersSearchParams({ expanded_order: null })
          }
        }
      } catch (loadError) {
        if (mounted) {
          setError(loadError.message)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadOrders()
    return () => {
      mounted = false
    }
  }, [expandedOrderId, page, updateOrdersSearchParams])

  const hasOrders = useMemo(() => orders.length > 0, [orders])
  const totalPages = useMemo(() => {
    if (!total) {
      return 0
    }
    return Math.max(1, Math.ceil(total / PAGE_SIZE))
  }, [total])
  const rangeStart = total ? (page - 1) * PAGE_SIZE + 1 : 0
  const rangeEnd = total ? Math.min(total, page * PAGE_SIZE) : 0

  const toggleExpanded = (orderId) => {
    updateOrdersSearchParams({
      expanded_order: expandedOrderId === orderId ? null : orderId
    })
  }

  const handleOrderUpdated = (updatedOrder) => {
    if (!updatedOrder?._id) {
      return
    }
    setOrders((currentOrders) => currentOrders.map(order => (
      order._id === updatedOrder._id ? updatedOrder : order
    )))
  }

  return (
    <Box
      sx={{
        width: '100%',
        marginTop: '20px',
        padding: '20px',
        border: '1px solid',
        borderColor: theme.palette.borderColor.main,
        borderRadius: '12px',
        backgroundColor: theme.palette.primary.main
      }}
    >
      <Typography variant='h6' sx={{ fontSize: '16px', fontWeight: 700, marginBottom: '10px' }}>
        Comenzile mele
      </Typography>
      <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginBottom: '18px', fontSize: '10.5px', lineHeight: 1.95 }}>
        Vezi rapid starea comenzilor și descarcă direct documentele din fiecare comandă.
      </Typography>

      {!loading && !error && hasOrders && (
        <Paper
          elevation={0}
          sx={{
            marginBottom: '18px',
            padding: '12px 16px',
            border: '1px solid',
            borderColor: theme.palette.borderColor.main,
            borderRadius: '12px',
            backgroundColor: theme.palette.secondary.main
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '13px', lineHeight: 1.5 }}>
                Comenzi afișate: {rangeStart}-{rangeEnd} din {total}
              </Typography>
              <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, fontSize: '11px', lineHeight: 1.8 }}>
                Apasă pe o comandă pentru detalii și documente.
              </Typography>
            </Box>
            {totalPages > 1 && (
              <Pagination
                page={page}
                count={totalPages}
                onChange={(_event, value) => updateOrdersSearchParams({ orders_page: value, expanded_order: null })}
              />
            )}
          </Stack>
        </Paper>
      )}

      {loading && <Typography>Se încarcă comenzile…</Typography>}
      {!loading && error && <Alert severity='error'>{error}</Alert>}
      {!loading && !error && !hasOrders && (
        <Alert severity='info'>Nu ai comenzi plasate încă.</Alert>
      )}

      {!loading && !error && hasOrders && (
        <Stack spacing={2}>
          {orders.map(order => {
            const isExpanded = expandedOrderId === order._id
            const previewItems = order.items?.slice(0, 1) || []
            const extraItemsCount = Math.max(0, (order.items?.length || 0) - previewItems.length)
            const documentActions = getDocumentActions(order)
            const displayOrderNumber = shortenOrderNumber(order.order_number)
            const onOrderItems = (order.items || []).filter(item => hasOnOrderQuantity(item, item.quantity))

            return (
              <Paper
                key={order._id}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: theme.palette.borderColor.main,
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ padding: '11px 13px' }}>
                  <Stack spacing={1.35}>
                    <Stack
                      direction={{ xs: 'column', lg: 'row' }}
                      justifyContent='space-between'
                      alignItems={{ xs: 'flex-start', lg: 'center' }}
                      spacing={1.75}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack direction='row' spacing={0.75} alignItems='center'>
                          <Typography
                            sx={{ fontSize: { xs: '12.5px', md: '13.5px' }, fontWeight: 800, lineHeight: 1.6, letterSpacing: '-0.003em' }}
                            title={order.order_number}
                          >
                            {displayOrderNumber}
                          </Typography>
                          <Tooltip title='Copiaza numarul comenzii'>
                            <IconButton
                              size='small'
                              aria-label='Copiaza numarul comenzii'
                              onClick={() => copyTextToClipboard(displayOrderNumber)}
                              sx={{
                                width: 24,
                                height: 24,
                                color: theme.palette.fontColor.medium,
                                '&:hover': { color: theme.palette.accent.main }
                              }}
                            >
                              <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {hasShortDisplayVariant(order.order_number) && (
                          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginTop: '2px', fontSize: '9.5px', lineHeight: 1.8 }}>
                            Ref completa: {order.order_number}
                          </Typography>
                        )}
                        <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginTop: '4px', fontSize: '10px', lineHeight: 2.05 }}>
                          Plasată la {new Date(order.created_at).toLocaleString('ro-RO')}
                        </Typography>
                      </Box>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                        sx={{ width: { xs: '100%', lg: 'auto' } }}
                      >
                        <Typography component='div' sx={{ fontSize: '12px', fontWeight: 800, color: theme.palette.accent.main, whiteSpace: 'nowrap', lineHeight: 1.6 }}>
                          <Currency locale='ro-RO' currency='RON'>{order.total_price}</Currency>
                        </Typography>
                        <Stack direction='row' spacing={1} flexWrap='wrap'>
                          <Chip
                            size='small'
                            label={paymentLabels[order.payment_status] || order.payment_status}
                            color={paymentChipColor[order.payment_status] || 'default'}
                            sx={{ height: '20px', '& .MuiChip-label': { px: 0.9, fontSize: '9.5px', fontWeight: 700, lineHeight: 1.6 } }}
                          />
                          <Chip
                            size='small'
                            label={fulfillmentLabels[order.fulfillment_status] || order.fulfillment_status}
                            color={fulfillmentChipColor[order.fulfillment_status] || 'default'}
                            sx={{ height: '20px', '& .MuiChip-label': { px: 0.9, fontSize: '9.5px', fontWeight: 700, lineHeight: 1.6 } }}
                          />
                          {order.payment_method === 'bank_transfer' && (
                            <Chip
                              size='small'
                              label={bankTransferLabels[order.bank_transfer_status] || order.bank_transfer_status}
                              color={bankTransferChipColor[order.bank_transfer_status] || 'default'}
                              sx={{ height: '20px', '& .MuiChip-label': { px: 0.9, fontSize: '9.5px', fontWeight: 700, lineHeight: 1.6 } }}
                            />
                          )}
                          {onOrderItems.length > 0 && (
                            <Chip
                              size='small'
                              label='Include la comanda'
                              color='warning'
                              sx={{ height: '20px', '& .MuiChip-label': { px: 0.9, fontSize: '9.5px', fontWeight: 700, lineHeight: 1.6 } }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Stack>

                    <Stack
                      direction='column'
                      spacing={1.1}
                    >
                      <Box sx={{ flex: 1 }}>
                        {previewItems.map(item => (
                          <Box key={`${order._id}-${item.item_id}`}>
                            <Typography variant='body2' sx={{ color: theme.palette.fontColor.main, fontSize: '10px', lineHeight: 1.95 }}>
                              {item.quantity} x {item.name}
                            </Typography>
                            <AvailabilitySplit item={item} />
                          </Box>
                        ))}
                        {extraItemsCount > 0 && (
                          <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginTop: '4px', fontSize: '10px', lineHeight: 1.9 }}>
                            + încă {extraItemsCount} produse
                          </Typography>
                        )}
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap='wrap'>
                        {documentActions.map(action => (
                          <Button
                            key={action.key}
                            component={action.href ? 'a' : 'button'}
                            href={action.href}
                            target={action.href ? '_blank' : undefined}
                            rel={action.href ? 'noreferrer' : undefined}
                            size='small'
                            variant={action.variant}
                            startIcon={action.icon}
                            onClick={action.onClick}
                            sx={action.sx}
                          >
                            {action.label}
                          </Button>
                        ))}
                        <Button
                          size='small'
                          variant='contained'
                          onClick={() => toggleExpanded(order._id)}
                          endIcon={isExpanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                          sx={getSecondaryActionSx()}
                        >
                          {isExpanded ? 'Ascunde detaliile' : 'Detalii și documente'}
                        </Button>
                      </Stack>
                      {order.payment_method === 'bank_transfer' && (
                        <Box>
                          <BankTransferProofUploader order={order} onUploaded={handleOrderUpdated} />
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                  <Divider />
                  <Box sx={{ padding: '13px 15px' }}>
                    <Stack spacing={1.75}>
                      {order.payment_method === 'bank_transfer' && (
                        <BankTransferDetailsCard
                          details={getBankTransferDetailsFromOrder(order)}
                          helperText='Descarca proforma, foloseste exact contul afisat mai jos pentru transfer si apoi incarca dovada platii.'
                        />
                      )}
                      {isPaidBankTransferWithoutInvoice(order) && (
                        <Alert severity='info' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>
                          Proforma ramane disponibila in cont. Pentru ordin de plata, factura finala este emisa separat de echipa Primusvest dupa confirmarea interna.
                        </Alert>
                      )}
                      {isPaidCardWithoutFinalInvoice(order) && (
                        <Alert severity='info' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>
                          Plata este confirmata. Documentul Stripe ramane dovada platii, iar factura finala Primusvest va deveni disponibila dupa procesare.
                        </Alert>
                      )}
                      {onOrderItems.length > 0 && (
                        <Alert severity='warning' sx={{ '& .MuiAlert-message': { fontSize: '10px', lineHeight: 1.7 } }}>
                          Comanda poate fi platita integral. Produsele marcate „La comanda” nu sunt complet pe stoc si pot avea termen de livrare mai mare.
                        </Alert>
                      )}
                      <Box>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, marginBottom: '10px', lineHeight: 1.8 }}>
                          Produse comandate
                        </Typography>
                        <Stack spacing={1}>
                          {order.items?.map(item => (
                            <Box
                              key={`${order._id}-${item.item_id}`}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1.5fr auto auto' },
                                gap: 1.5,
                                alignItems: 'start'
                              }}
                            >
                              <Typography variant='body2' sx={{ fontSize: '10px', lineHeight: 1.95 }}>
                                {item.quantity} x {item.name}
                              </Typography>
                              <Box sx={{ color: theme.palette.fontColor.medium, fontSize: '10px', lineHeight: 1.9 }}>
                                <AvailabilitySplit item={item} />
                              </Box>
                              <Typography component='div' variant='body2' sx={{ fontWeight: 600, textAlign: { xs: 'left', md: 'right' }, fontSize: '10px', lineHeight: 1.9 }}>
                                <Currency locale='ro-RO' currency='RON'>{item.total_price}</Currency>
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                      <Divider />

                      <Stack spacing={1}>
                        <Typography variant='body2' sx={{ fontSize: '10px', lineHeight: 1.95 }}>
                          <strong>Livrare:</strong> {order.delivery_snapshot?.full_address || order.billing_snapshot?.full_address || 'Adresa nu este disponibilă încă.'}
                        </Typography>
                        <Typography variant='body2' sx={{ fontSize: '10px', lineHeight: 1.95 }}>
                          <strong>Facturare:</strong> {order.billing_snapshot?.full_address || 'Adresa de facturare nu este disponibilă încă.'}
                        </Typography>
                      </Stack>

                      <Divider />

                      <Box sx={{ alignSelf: 'flex-end', width: { xs: '100%', sm: '320px' } }}>
                        <Stack spacing={1}>
                          <Stack direction='row' justifyContent='space-between'>
                            <Typography variant='body2' sx={{ fontSize: '10px', lineHeight: 1.9 }}>Subtotal</Typography>
                            <Typography component='div' variant='body2' sx={{ fontSize: '10px', lineHeight: 1.9 }}><Currency locale='ro-RO' currency='RON'>{order.subtotal_price}</Currency></Typography>
                          </Stack>
                          <Stack direction='row' justifyContent='space-between'>
                            <Typography variant='body2' sx={{ fontSize: '10px', lineHeight: 1.9 }}>Transport</Typography>
                            <Typography component='div' variant='body2' sx={{ fontSize: '10px', lineHeight: 1.9 }}><Currency locale='ro-RO' currency='RON'>{order.shipping_price}</Currency></Typography>
                          </Stack>
                          <Stack direction='row' justifyContent='space-between'>
                            <Typography sx={{ fontWeight: 700, fontSize: '11px', lineHeight: 1.8 }}>Total</Typography>
                            <Typography component='div' sx={{ fontWeight: 700, color: theme.palette.accent.main, fontSize: '11px', lineHeight: 1.8 }}>
                              <Currency locale='ro-RO' currency='RON'>{order.total_price}</Currency>
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Collapse>
              </Paper>
            )
          })}
        </Stack>
      )}

      {!loading && !error && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_event, value) => updateOrdersSearchParams({ orders_page: value, expanded_order: null })}
          />
        </Box>
      )}
    </Box>
  )
}

export default OrdersList
